import { Heading } from '@aws-amplify/ui-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  signOut as amplifySignOut,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  SignInInput,
  SignInOutput,
  signIn,
} from 'aws-amplify/auth';
import classNames from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { getUserProperties } from './api';
import { getUserPermissions } from './api/user-permissions';
import withAuthenticator from './auth-provider';
import LoadingScreen from './common-components/loading-screen/loading-screen';
import { useAppDispatch, useAppSelector, useMobile } from './hooks';
import useAccessControl from './hooks/access-control/useAccessControl';
import type { MapExtent } from './map/map.slice';
import MapHelpers from './map/map.utils';
import { setIsGlobalAisServiceOn } from './maritime-menu-options/fleets-panel/fleets-panel.slice';
import './require-auth.scss';
import {
  setApplySetupBarError,
  setIsLoading,
} from './settings/apply-setup-bar.slice';
import {
  setUserPermissions,
  setUserPermissionsInitialised,
} from './settings/user-permissions.slice';
import {
  DEFAULT_USER_PREFERENCES,
  setMapSetupLoaded,
  setUserPreferences,
  setUserPreferencesLoading,
  VesselPrefColour,
  VesselPrefLabel,
  VesselPrefSize,
} from './settings/user-preferences.slice';
import { RESET_ACTION } from './store';
import TenantId from './tenant';
import {
  setShowLiteModePopup,
  setUserFeatureFlags,
  setUserIdToken,
  setUserIdTokenError,
} from './user/user.slice';
import loadLiteMode from './utils/lite-mode.utils';
import { UserPreferencesBaseMap } from './utils/user.enum';
import {
  loadMapSavedSetup,
  setDefaultFiltersForUser,
} from './utils/user.utils';

type RequireAuthProps = {
  children: React.ReactNode;
};

function RequireAuth({ children }: RequireAuthProps) {
  const mapInitialised = useAppSelector((state) => state.map.mapInitialised);
  const userPreferences = useAppSelector(
    (state) => state.userPreferences.userPreferences
  );
  const userPreferencesLoading = useAppSelector(
    (state) => state.userPreferences.userPreferencesLoading
  );
  const mapSetupLoaded = useAppSelector(
    (state) => state.userPreferences.mapSetupLoaded
  );
  const liteMode = useAppSelector((state) => state.user.liteMode);
  const userSigningOut = useAppSelector((state) => state.user.userSigningOut);
  const idToken = useAppSelector((state) => state.user.idToken);
  const { userPermissionsInitialised } = useAppSelector(
    (state) => state.userPermissions
  );
  const signOutEventReceived = useAppSelector(
    (state) => state.webSocketEvents.signOutEventReceived
  );

  const cognitoId = useAppSelector((state) => state.user.idToken?.sub);
  const dispatch = useAppDispatch();
  const privileges = useAccessControl();

  const liteModeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const signOut = async () => {
    await amplifySignOut();
    dispatch(RESET_ACTION);
  };

  useEffect(() => {
    if (signOutEventReceived || userSigningOut) {
      signOut();
    }
  }, [signOutEventReceived, userSigningOut]);

  const checkTokensRevoked = async () => {
    try {
      // Bypass the local cache to check whether the current tokens have been revoked in Cognito
      // If they have been revoked, this will throw an error.
      await getCurrentUser();
    } catch {
      // Clear local tokens
      await signOut();
    }
  };

  const fetchUserIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.payload;
      return token;
    } catch (error) {
      dispatch(setUserIdTokenError(error));
      return null;
    }
  };

  const fetchIdTokenAndUserPermissions = async () => {
    const cognitoIdToken = await fetchUserIdToken();

    const canFetchUserPermissions =
      cognitoIdToken?.['custom:tenantId'] === TenantId.GEOLLECT;

    if (canFetchUserPermissions && !userPermissionsInitialised) {
      const userPermissions = await getUserPermissions();
      dispatch(setUserPermissions(userPermissions));
    }

    dispatch(setUserIdToken(cognitoIdToken));
    dispatch(setUserPermissionsInitialised(true));
  };

  const loadUserPreferencesAndFeatureFlags = useCallback(async () => {
    try {
      if (cognitoId) {
        const preferencesResponse = await getUserProperties(cognitoId);
        const featureFlags = preferencesResponse[0].feature_flags;
        const preferences = preferencesResponse[0].user_preferences;

        MapHelpers.updateMapStyle(
          (preferences?.basemap as UserPreferencesBaseMap) ||
            UserPreferencesBaseMap.DEFAULT
        );
        MapHelpers.updateMapExtent(
          (preferences?.mapExtent as MapExtent) ||
            DEFAULT_USER_PREFERENCES.mapExtent
        );

        setDefaultFiltersForUser();
        dispatch(setUserFeatureFlags(featureFlags));
        dispatch(
          setUserPreferences({
            // we default global ais to on unless specifically set to off
            // this prevents requirement of data migration to add this to new users
            globalAisPanel: DEFAULT_USER_PREFERENCES.globalAisPanel,
            ...preferences,
            vessel_preference: preferences?.vessel_preference ?? {
              vesselLabel: VesselPrefLabel.HIDE,
              vesselSize: VesselPrefSize.STANDARD,
              vesselColour: VesselPrefColour.NAVY,
            },
            zoomToIncident:
              preferences?.zoomToIncident !== undefined
                ? preferences.zoomToIncident
                : true,
            basemap: preferences?.basemap ?? UserPreferencesBaseMap.DEFAULT,
          })
        );
        dispatch(
          setIsGlobalAisServiceOn(
            preferences.fleetsPanel?.isGlobalAisServiceOn ?? true
          )
        );
      }
    } catch {
      dispatch(setApplySetupBarError(true));

      dispatch(setUserPreferences(DEFAULT_USER_PREFERENCES));
    } finally {
      dispatch(setUserPreferencesLoading(false));
    }
  }, [cognitoId]);

  const clearLiteModeTimer = () => clearTimeout(liteModeTimer?.current!);

  useEffect(() => {
    (async () => {
      await checkTokensRevoked();
      await fetchIdTokenAndUserPermissions();
    })();
  }, []);

  useEffect(() => {
    if (mapInitialised) {
      let loader: Promise<any> | undefined;
      if (liteMode) {
        loader = loadLiteMode(dispatch).then(() =>
          dispatch(setIsLoading(false))
        );
      } else if (
        !userPreferencesLoading &&
        !mapSetupLoaded &&
        !!idToken // must check that an idToken exists - stops attempting to load map setup before user is logged in
      ) {
        loader = loadMapSavedSetup(userPreferences, dispatch, privileges).then(
          () => clearLiteModeTimer()
        );
      }

      if (loader) {
        loader.then(() => dispatch(setMapSetupLoaded(true)));
      }
    }
  }, [
    !userPreferencesLoading,
    mapInitialised,
    userPreferences,
    mapSetupLoaded,
    liteMode,
    idToken,
  ]);

  useEffect(() => {
    dispatch(setUserPreferencesLoading(true));

    if (idToken?.tenantId) {
      loadUserPreferencesAndFeatureFlags();
    }
  }, [loadUserPreferencesAndFeatureFlags]);

  useEffect(() => {
    if (mapInitialised && !liteMode) {
      liteModeTimer.current = setTimeout(
        () => dispatch(setShowLiteModePopup(true)),
        10000
      ); // show popup after 10 seconds
    }

    return () => {
      clearLiteModeTimer();
    };
  }, [mapInitialised, liteMode]);

  const isLoaded =
    idToken &&
    userPermissionsInitialised &&
    userPreferences &&
    !userPreferencesLoading;

  return (
    <>
      {!isLoaded && <LoadingScreen />}
      {isLoaded && children}
    </>
  );
}

function AuthHeader() {
  const isMobile = useMobile();

  return (
    <Box
      className={classNames('auth-header', {
        mobile: isMobile,
      })}
    />
  );
}

function AuthHeading({
  children,
  signIn = false,
}: {
  children: React.ReactNode;
  signIn?: boolean;
}) {
  return (
    <Heading
      level={3}
      className={classNames('custom-auth-header', {
        'custom-auth-header--signin': signIn,
      })}
    >
      <Typography className="auth-heading-text">{children}</Typography>
    </Heading>
  );
}

/**
 * Removes all entries from localStorage whose keys include 'deviceKey'.
 *
 * This function should be called when the current locally stored device key is determined
 * to be invalid, in order to prevent "Device does not Exist" errors from occurring.
 *
 * If a user is being asked to sign in then this is the case.
 */
function clearCognitoDeviceKeys() {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes('deviceKey')) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Handles the sign-in process for a user.
 *
 * Checks if a user is trying to login with non-Geollect credentials.
 *
 * @param formData - An object containing the user's credentials.
 * @param formData.username - The username of the user.
 * @param formData.password - The password of the user.
 * @returns A promise that resolves to the authenticated user object.
 * @throws An error if the user's tenant ID is not `TenantId.GEOLLECT`.
 */

const handleSignIn = async (input: SignInInput): Promise<SignInOutput> => {
  clearCognitoDeviceKeys();

  const { username, password } = input;

  const signInResult = await signIn({ username, password });

  // If a user is signing in for the first time, Cognito will require them to set a new password.
  // Cognito will return 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' as the signInStep and show the new password form.
  // Once the user has set their new password, Cognito will complete the sign-in and return 'DONE'.
  if (signInResult.nextStep.signInStep !== 'DONE') {
    return signInResult;
  }

  const attributes = await fetchUserAttributes();
  const tenantId = attributes['custom:tenantId'];

  if (tenantId && tenantId !== TenantId.GEOLLECT) {
    throw new Error('Invalid user credentials provided');
  }

  return signInResult;
};

export default withAuthenticator(RequireAuth, {
  className: 'login-container',
  formFields: {
    signIn: {
      username: {
        label: undefined,
        placeholder: 'Username',
        required: true,
        type: 'username',
        autocomplete: 'username',
      },
      password: {
        label: undefined,
        placeholder: 'Password',
        required: true,
        type: 'password',
        autocomplete: 'current-password',
      },
    },
    forgotPassword: {
      username: { placeholder: 'Username', labelHidden: true },
    },
    confirmResetPassword: {
      confirmation_code: { placeholder: 'Code', labelHidden: true },
      password: { placeholder: 'New Password', labelHidden: true },
      confirm_password: { placeholder: 'Confirm Password', labelHidden: true },
    },
  },
  components: {
    Header: () => <AuthHeader />,
    SignIn: {
      Header: () => <AuthHeading signIn>Sign In</AuthHeading>,
    },
    ForgotPassword: {
      Header: () => <AuthHeading>Reset Password</AuthHeading>,
    },
    ConfirmResetPassword: {
      Header: () => <AuthHeading>Reset Password</AuthHeading>,
    },
  },
  hideSignUp: true,
  initialState: 'signIn',
  services: {
    handleSignIn,
  },
});
